/**
 * OffersCarousel - Componente de carrossel horizontal responsivo
 * 
 * Funcionalidades:
 * - Exibe até 6 cards por vez na tela
 * - Ativa carrossel automático quando há mais de 6 cards
 * - Autoplay com rolagem suave contínua
 * - Pausa ao passar o mouse (hover)
 * - Loop infinito
 * - Suporte a drag (desktop) e touch (mobile)
 * - Totalmente responsivo
 */

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import type { Product, SpecialOffer } from '@/types';
import { CartIcon, CheckIcon } from '@/pages/Catshop/CatshopPage';
import './OffersCarousel.css';

// Constantes de configuração do carrossel
const CARDS_PER_VIEW = 6; // Número máximo de cards visíveis
const AUTOPLAY_INTERVAL = 4000; // Intervalo do autoplay em ms
const TRANSITION_DURATION = 500; // Duração da transição em ms
const DRAG_THRESHOLD = 50; // Limite mínimo de pixels para considerar um drag

interface OffersCarouselProps {
  /** Lista de produtos em oferta */
  products: Product[];
  /** Ofertas ativas (vindas do admin) */
  activeOffers: SpecialOffer[];
  /** Função para calcular preço com desconto */
  calculateDiscountedPrice: (price: string, discount: number) => string;
  /** Função para adicionar ao carrinho */
  onAddToCart: (product: Product) => void;
  /** Mapa de produtos adicionados (para feedback visual) */
  addedProducts: Record<number, boolean>;
  /** Renderizador customizado de imagem do produto */
  renderProductImage: (product: Product) => React.ReactNode;
  /** Função para obter info de preço do produto */
  getPriceInfo: (product: Product) => {
    original: string | undefined;
    final: string;
    discount: number | undefined;
  };
  /** Estado de loading */
  isLoading?: boolean;
  /** Callback opcional para clique no produto (abrir modal, etc.) */
  onProductClick?: (product: Product) => void;
}

// Utilitário para separar moeda do valor
const splitPriceLabel = (rawPrice: string) => {
  if (!rawPrice) {
    return { currency: 'R$', amount: '0,00' };
  }

  const match = rawPrice.trim().match(/^([^0-9,]+)?\s*(.+)$/);
  if (!match) {
    return { currency: 'R$', amount: rawPrice.trim() };
  }

  const currency = match[1]?.trim() || 'R$';
  const amount = match[2]?.trim() || '0,00';
  return { currency, amount };
};

export const OffersCarousel: React.FC<OffersCarouselProps> = ({
  products,
  activeOffers,
  onAddToCart,
  addedProducts,
  renderProductImage,
  getPriceInfo,
  isLoading = false,
  onProductClick,
}) => {
  // Estado do carrossel
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Refs
  const carouselRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const autoplayRef = useRef<NodeJS.Timeout | null>(null);

  // Determina se deve usar modo carrossel (mais de 6 produtos)
  const useCarouselMode = products.length > CARDS_PER_VIEW;

  // Para loop infinito, duplicamos os produtos no início e no fim
  const extendedProducts = useMemo(() => {
    if (!useCarouselMode) return products;
    
    // Cria cópias para loop infinito: [últimos N] + [todos] + [primeiros N]
    const cloneCount = Math.min(CARDS_PER_VIEW, products.length);
    const startClones = products.slice(-cloneCount);
    const endClones = products.slice(0, cloneCount);
    
    return [...startClones, ...products, ...endClones];
  }, [products, useCarouselMode]);

  // Índice real considerando os clones do início
  const realIndex = useMemo(() => {
    if (!useCarouselMode) return 0;
    return Math.min(CARDS_PER_VIEW, products.length);
  }, [products.length, useCarouselMode]);

  // Calcula o número de cards visíveis baseado no viewport
  const [visibleCards, setVisibleCards] = useState(CARDS_PER_VIEW);

  // Atualiza número de cards visíveis baseado no tamanho da tela
  useEffect(() => {
    const updateVisibleCards = () => {
      const width = window.innerWidth;
      if (width < 480) {
        setVisibleCards(1);
      } else if (width < 640) {
        setVisibleCards(2);
      } else if (width < 768) {
        setVisibleCards(3);
      } else if (width < 1024) {
        setVisibleCards(4);
      } else if (width < 1280) {
        setVisibleCards(5);
      } else {
        setVisibleCards(CARDS_PER_VIEW);
      }
    };

    updateVisibleCards();
    window.addEventListener('resize', updateVisibleCards);
    return () => window.removeEventListener('resize', updateVisibleCards);
  }, []);

  // Inicializa o índice com offset dos clones
  useEffect(() => {
    if (useCarouselMode) {
      setCurrentIndex(realIndex);
    }
  }, [useCarouselMode, realIndex]);

  // Função para mover o carrossel
  const moveToIndex = useCallback((index: number, animate = true) => {
    if (!useCarouselMode) return;

    setIsTransitioning(animate);
    setCurrentIndex(index);

    // Se chegou no fim ou início dos clones, reposiciona sem animação
    if (animate) {
      setTimeout(() => {
        const maxIndex = products.length + realIndex - 1;
        const minIndex = realIndex;

        if (index >= maxIndex) {
          setIsTransitioning(false);
          setCurrentIndex(minIndex);
        } else if (index < minIndex) {
          setIsTransitioning(false);
          setCurrentIndex(maxIndex - 1);
        }
      }, TRANSITION_DURATION);
    }
  }, [useCarouselMode, products.length, realIndex]);

  // Navega para o próximo slide
  const nextSlide = useCallback(() => {
    moveToIndex(currentIndex + 1);
  }, [currentIndex, moveToIndex]);

  // Navega para o slide anterior
  const prevSlide = useCallback(() => {
    moveToIndex(currentIndex - 1);
  }, [currentIndex, moveToIndex]);

  // Controle do autoplay
  useEffect(() => {
    if (!useCarouselMode || isPaused || isDragging) {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
        autoplayRef.current = null;
      }
      return;
    }

    autoplayRef.current = setInterval(nextSlide, AUTOPLAY_INTERVAL);

    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
      }
    };
  }, [useCarouselMode, isPaused, isDragging, nextSlide]);

  // Handlers de mouse para pause no hover
  const handleMouseEnter = useCallback(() => {
    setIsPaused(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsPaused(false);
    setIsDragging(false);
    setDragOffset(0);
  }, []);

  // Handlers de drag (desktop)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!useCarouselMode) return;
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragOffset(0);
  }, [useCarouselMode]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || !useCarouselMode) return;
    const diff = e.clientX - dragStartX;
    setDragOffset(diff);
  }, [isDragging, dragStartX, useCarouselMode]);

  const handleMouseUp = useCallback(() => {
    if (!isDragging || !useCarouselMode) return;

    if (Math.abs(dragOffset) > DRAG_THRESHOLD) {
      if (dragOffset > 0) {
        prevSlide();
      } else {
        nextSlide();
      }
    }

    setIsDragging(false);
    setDragOffset(0);
  }, [isDragging, dragOffset, prevSlide, nextSlide, useCarouselMode]);

  // Handlers de touch (mobile)
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!useCarouselMode) return;
    setIsDragging(true);
    setIsPaused(true);
    setDragStartX(e.touches[0].clientX);
    setDragOffset(0);
  }, [useCarouselMode]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging || !useCarouselMode) return;
    const diff = e.touches[0].clientX - dragStartX;
    setDragOffset(diff);
  }, [isDragging, dragStartX, useCarouselMode]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging || !useCarouselMode) return;

    if (Math.abs(dragOffset) > DRAG_THRESHOLD) {
      if (dragOffset > 0) {
        prevSlide();
      } else {
        nextSlide();
      }
    }

    setIsDragging(false);
    setDragOffset(0);
    setIsPaused(false);
  }, [isDragging, dragOffset, prevSlide, nextSlide, useCarouselMode]);

  // Calcula o estilo de transformação do track
  const trackStyle = useMemo(() => {
    if (!useCarouselMode) {
      return {};
    }

    const cardWidth = 100 / visibleCards;
    const translateX = -(currentIndex * cardWidth) + (dragOffset / (carouselRef.current?.offsetWidth || 1)) * 100;

    return {
      transform: `translateX(${translateX}%)`,
      transition: isTransitioning && !isDragging ? `transform ${TRANSITION_DURATION}ms ease-in-out` : 'none',
    };
  }, [currentIndex, dragOffset, isTransitioning, isDragging, useCarouselMode, visibleCards]);

  // Renderiza card individual
  const renderCard = useCallback((product: Product, index: number) => {
    const adminOffer = activeOffers.find((offer: SpecialOffer) => offer.productId === product.id);
    const discountPercentage = adminOffer ? adminOffer.discountPercentage : (product.discount || 0);
    const priceInfo = getPriceInfo(product);
    const currentPriceLabel = priceInfo.final;
    const priceParts = splitPriceLabel(currentPriceLabel);

    return (
      <div
        key={`${product.id}-${index}`}
        className={`carousel-card offer-highlight ${!(product.inStock ?? true) ? 'out-of-stock' : ''}`}
        style={{ width: `${100 / visibleCards}%` }}
      >
        <div className="carousel-card-inner">
          {/* Faixa principal de oferta no topo do card */}
          <div className="offer-title-badge">OFERTA</div>

          {/* Imagem do produto */}
          <div className="product-media">
            <div className="product-media__frame">
              <div
                className="product-media__content"
                onClick={onProductClick ? () => onProductClick(product) : undefined}
                role={onProductClick ? 'button' : undefined}
                tabIndex={onProductClick ? 0 : undefined}
                onKeyDown={onProductClick ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onProductClick(product);
                  }
                } : undefined}
              >
                {renderProductImage(product)}
              </div>
            </div>
          </div>

          {/* Corpo do card */}
          <div className="product-body">
            <h3 className="product-title">{product.name}</h3>
            <p className="product-description">{product.description}</p>
          </div>

          {/* Footer do card */}
          <div className="product-footer">
            <div className="product-price">
              <div className="product-price__current">
                <span className="product-price__currency">{priceParts.currency}</span>
                <span className="product-price__amount">{priceParts.amount}</span>
              </div>
              {priceInfo.original && (
                <div className="product-price-details">
                  <span className="original-price">{priceInfo.original}</span>
                  {discountPercentage > 0 && (
                    <span className="discount-badge">-{discountPercentage}%</span>
                  )}
                </div>
              )}
            </div>

            {/* Descrição da oferta */}
            {adminOffer && adminOffer.description && (
              <div className="offer-description-mini">{adminOffer.description}</div>
            )}

            {/* Badge sem estoque */}
            {!(product.inStock ?? true) && (
              <div className="out-of-stock-badge">❌ Sem Estoque</div>
            )}

            {/* Botão adicionar ao carrinho */}
            <div className="product-actions">
              <button
                className={`add-to-cart-button ${addedProducts[product.id] ? 'added' : ''}`}
                onClick={() => onAddToCart(product)}
                disabled={product.inStock === false}
                aria-label={addedProducts[product.id]
                  ? `Produto ${product.name} adicionado.`
                  : `Adicionar ${product.name} ao carrinho`
                }
                type="button"
              >
                <span className="button-content">
                  {addedProducts[product.id] ? <CheckIcon /> : <CartIcon />}
                  <span className="button-label" aria-live="polite">
                    {product.inStock !== false
                      ? (addedProducts[product.id] ? 'Adicionado' : 'Adicionar ao Carrinho')
                      : 'Indisponível'
                    }
                  </span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }, [activeOffers, addedProducts, getPriceInfo, onAddToCart, onProductClick, renderProductImage, visibleCards]);

  // Não renderiza se estiver carregando ou sem produtos
  if (isLoading || products.length === 0) {
    return null;
  }

  // Modo grade simples (até 6 produtos)
  if (!useCarouselMode) {
    return (
      <div className="offers-carousel-section">
        <h3 className="offers-carousel-title">Ofertas Especiais</h3>
        <div className="offers-grid">
          {products.map((product, index) => renderCard(product, index))}
        </div>
      </div>
    );
  }

  // Modo carrossel (mais de 6 produtos)
  return (
    <div className="offers-carousel-section">
      <h3 className="offers-carousel-title">Ofertas Especiais</h3>
      
      <div
        ref={carouselRef}
        className={`offers-carousel ${isDragging ? 'dragging' : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Botão anterior */}
        <button
          className="carousel-nav carousel-nav--prev"
          onClick={prevSlide}
          aria-label="Slide anterior"
          type="button"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        {/* Container do carrossel */}
        <div className="carousel-viewport">
          <div
            ref={trackRef}
            className="carousel-track"
            style={trackStyle}
          >
            {extendedProducts.map((product, index) => renderCard(product, index))}
          </div>
        </div>

        {/* Botão próximo */}
        <button
          className="carousel-nav carousel-nav--next"
          onClick={nextSlide}
          aria-label="Próximo slide"
          type="button"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>

      {/* Indicadores de navegação */}
      <div className="carousel-indicators">
        {products.map((_, index) => (
          <button
            key={index}
            className={`carousel-indicator ${
              (currentIndex - realIndex) % products.length === index ? 'active' : ''
            }`}
            onClick={() => moveToIndex(realIndex + index)}
            aria-label={`Ir para slide ${index + 1}`}
            type="button"
          />
        ))}
      </div>
    </div>
  );
};

export default OffersCarousel;
